import { AuthConnection, Region } from './hooks/useCreateAccount';

export type AccountDetailsState = {
  displayName: string;
  adminEmail?: string;
  region: Region;
  // undefined selects invite-only login; a string (even '') selects domain login
  loginDomain?: string;
  authConnections: Record<AuthConnection, boolean>;
};

export const NAME_MAX_LENGTH = 21;
export const NAME_MIN_LENGTH = 3;

export const DEFAULT_STATE: AccountDetailsState = {
  displayName: '',
  region: Region.RegionEu,
  loginDomain: '',
  authConnections: {
    googleAuthConnection: false,
    passwordAuthConnection: false,
  },
};

const displayNameRegex = /^[a-zA-Z][a-zA-Z0-9 -]*[a-zA-Z0-9]$/;

export const isDisplayNameValid = (displayName: string) => {
  return (
    displayNameRegex.test(displayName) &&
    displayName.length >= NAME_MIN_LENGTH &&
    displayName.length <= NAME_MAX_LENGTH
  );
};

export const formatDisplayName = (displayName: string) => {
  return displayName.replace(/[^a-zA-Z0-9 -]/g, '').slice(0, NAME_MAX_LENGTH);
};

export const formatLoginId = (loginId: string = '') => {
  return loginId
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase()
    .slice(0, NAME_MAX_LENGTH);
};

const loginIdRegex = /^[a-z][a-z0-9-]*[a-z0-9]$/;

export const isLoginIdValid = (loginId: string) => {
  return (
    loginIdRegex.test(loginId) &&
    loginId.length >= NAME_MIN_LENGTH &&
    loginId.length <= NAME_MAX_LENGTH
  );
};

const nameLengthMessage = `The name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`;

export const getWorkspaceNameHelperText = ({
  displayName,
  loginId,
  isModified,
  isAvailable,
  availabilityError,
}: {
  displayName: string;
  loginId: string;
  isModified: boolean;
  isAvailable: boolean;
  availabilityError?: string;
}): string => {
  if (isModified && !/^[a-zA-Z]/.test(displayName)) {
    return 'The name should start with a letter.';
  }
  if (isModified && (displayName.endsWith('-') || displayName.endsWith(' '))) {
    return 'The name cannot end with "-" or a space.';
  }
  if (
    isModified &&
    !isDisplayNameValid(displayName) &&
    displayName.length > 0
  ) {
    return nameLengthMessage;
  }
  if (!isAvailable) {
    return 'This workspace name is already in use.';
  }
  if (availabilityError) {
    return availabilityError;
  }
  if (loginId) {
    return `Your workspace ID will be "${loginId}". You'll need this to sign in.`;
  }
  return 'Shown across Confidence and to your teammates.';
};

export const isEmailNotVerifiedError = (error?: Error) => {
  return Boolean(error?.message.includes('Email not verified'));
};

export const isAccountUnderReviewError = (error?: Error) => {
  return Boolean(error && /review/i.test(error.message));
};
